import { Card } from "@/components/flexkit/Card";
import { useGetWorkflowExecution, type PlainWorkflowExecution } from "@/queries/automation-queries";
import { useState, type FC } from "react";
import { Text } from "@/components/podkit/typography/Text";
import { cn } from "@/components/podkit/lib/cn";
import { durationBetweenTimestamps } from "@/format/time";
import { WorkflowExecutionActionsDropdown } from "@/components/automations/AutomationExecutionActionsDropdown";
import { ExecutionMetrics } from "@/components/automations/AutomationExecutionMetrics";
import { ExecutionTriggerInfo } from "@/components/automations/AutomationExecutionTriggerInfo";
import { IconCheckFill } from "@/assets/icons/geist/IconCheckFill";
import { IconError } from "@/assets/icons/geist/IconError";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { IconDot } from "@/assets/icons/geist/IconDot";
import { IconDotLarge } from "@/assets/icons/geist/IconDotLarge";
import IconCircle from "central-icons/IconCircle";
import IconStopCircle from "central-icons/IconStopCircle";
import { WorkflowExecutionPhase } from "gitpod-next-api/gitpod/v1/workflow_pb";
import { hasFailedActions, hasFailure, executionDisplayName, workflowExecutionPhaseLabel } from "@/utils/automations";
import { Tooltip } from "@/components/Tooltip";

type WorkflowExecutionsCardProps = {
    executionId: string;
    readonly?: boolean;
};

export const WorkflowExecutionsCard: FC<WorkflowExecutionsCardProps> = ({ executionId, readonly = false }) => {
    const { data: execution } = useGetWorkflowExecution(executionId);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    if (!execution) {
        return null;
    }

    const duration =
        execution.metadata?.startedAt && execution.metadata?.finishedAt
            ? durationBetweenTimestamps(execution.metadata.startedAt, execution.metadata.finishedAt)
            : undefined;

    const totalActionsCount =
        (execution.status?.doneActionCount ?? 0) +
        (execution.status?.runningActionCount ?? 0) +
        (execution.status?.pendingActionCount ?? 0) +
        (execution.status?.failedActionCount ?? 0) +
        (execution.status?.stoppedActionCount ?? 0);

    const getStatusIcon = () => {
        const phase = execution.status?.phase;

        switch (phase) {
            case WorkflowExecutionPhase.COMPLETED:
                return hasFailedActions(execution) ? (
                    <IconError size="base" className="flex-shrink-0 text-content-destructive" />
                ) : (
                    <IconCheckFill size="base" className="flex-shrink-0 text-content-success" />
                );
            case WorkflowExecutionPhase.RUNNING:
                return <IconDotLarge size="base" className="flex-shrink-0 animate-pulse text-content-warning" />;
            case WorkflowExecutionPhase.PENDING:
                return <IconDot size="sm" className="flex-shrink-0 text-content-tertiary" />;
            case WorkflowExecutionPhase.STOPPING:
                return <IconSpinner size="sm" className="flex-shrink-0 animate-spin text-content-warning" />;
            case WorkflowExecutionPhase.STOPPED:
                return hasFailure(execution) ? (
                    <IconError size="base" className="flex-shrink-0 text-content-destructive" />
                ) : (
                    <IconStopCircle size={20} className="flex-shrink-0 text-content-muted" />
                );
            default:
                return <IconCircle size={20} className="flex-shrink-0 text-content-muted" />;
        }
    };

    return (
        <Card
            key={execution.id}
            variant={"bordered"}
            className={cn("relative border bg-surface-secondary transition-shadow @container", {
                "hover:cursor-pointer hover:shadow-sm": !readonly,
                "shadow-sm": isDropdownOpen,
            })}
        >
            <div className="flex flex-col gap-3 @2xl:flex-row @2xl:items-center @2xl:justify-between @2xl:gap-6">
                {/* Left section: Status, ID, and trigger info */}
                <div className="flex min-w-0 flex-1 flex-col gap-2 pr-10 @2xl:pr-0">
                    <div className="flex items-center gap-3">
                        <Tooltip content={workflowExecutionPhaseLabel(execution)} usePortal>
                            <div>{getStatusIcon()}</div>
                        </Tooltip>
                        <ExecutionName execution={execution} />
                    </div>

                    <div className="pl-8">
                        <ExecutionTriggerInfo execution={execution} />
                    </div>

                    {/* Metrics - below trigger info on small screens */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pl-8 @2xl:hidden">
                        <ExecutionMetrics
                            execution={execution}
                            duration={duration}
                            totalActionsCount={totalActionsCount}
                        />
                    </div>
                </div>

                {/* Right section: Metrics on large screens */}
                <div className="hidden @2xl:flex @2xl:flex-shrink-0 @2xl:items-center @2xl:gap-4">
                    <ExecutionMetrics execution={execution} duration={duration} totalActionsCount={totalActionsCount} />
                </div>

                {/* More menu - top-right on small screens, inline on large screens */}
                {!readonly && (
                    <div className="absolute right-3 top-3 @2xl:relative @2xl:right-auto @2xl:top-auto @2xl:flex-shrink-0">
                        <WorkflowExecutionActionsDropdown execution={execution} onOpenChange={setIsDropdownOpen} />
                    </div>
                )}
            </div>
        </Card>
    );
};

const ExecutionName: FC<{ execution: PlainWorkflowExecution }> = ({ execution }) => {
    const displayName = executionDisplayName(execution);

    if (displayName.type === "pullRequest") {
        const { pr } = displayName;
        const prLabel = `PR #${pr.id}: ${pr.title || "Untitled"}`;
        return (
            <Text className="break-all text-base text-content-primary">
                <span className="text-content-secondary">[Webhook]</span>{" "}
                {pr.url ? (
                    <a
                        href={pr.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                        onClick={(e) => e.stopPropagation()}
                        data-tracking-id="pr-link"
                    >
                        {prLabel}
                    </a>
                ) : (
                    <span>{prLabel}</span>
                )}
            </Text>
        );
    }

    return (
        <Text className="break-all text-base text-content-primary">
            {displayName.label} {displayName.id}
        </Text>
    );
};
